import React, {useEffect, useRef, useState} from "react";
import {toast} from "react-toastify";
import ApiDatabase from "server";
import UseContext from "hooks/useContext";
import useModal from "components/modal/useModal";
import {
    buildLicenceCatalogueOptions,
    filterLicenceOptions,
    findLicenceOption,
    formatLicenceLabel,
    isSameLicence,
} from "helper/licences";
import LicenceCatalogueStep from "components/modal/dataModal/data/modaljustify/licence/LicenceCatalogueStep";
import LicencePhotoStep from "components/modal/dataModal/data/modaljustify/licence/LicencePhotoStep";
import LicenceConditionsStep from "components/modal/dataModal/data/modaljustify/licence/LicenceConditionsStep";

const KNOWN_ERROR_CODES = ["LICENCE_NOT_IN_CATALOGUE"];

const toErrorKey = (error) => {
    const errorCode = error?.response?.data?.errorCode;
    return KNOWN_ERROR_CODES.includes(errorCode) ? errorCode : "default";
};

const Modallicence = () => {
    const {
        language,
        refreshLoadWorker,
        isWebcamToUse,
        setIsWebcamToUse,
        licenceModalPreset,
        setLicenceModalPreset,
    } = UseContext();
    const {toggle} = useModal();

    // Read once: the preset only describes how the modal opens.
    const [preset] = useState(licenceModalPreset);
    const [step, setStep] = useState("catalogue");
    const [catalogueOptions, setCatalogueOptions] = useState([]);
    const [catalogueStatus, setCatalogueStatus] = useState("loading");
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedKey, setSelectedKey] = useState(null);
    const [file, setFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [errorKey, setErrorKey] = useState(null);

    // The upload can outlast the modal: never set state once it is gone.
    const isMountedRef = useRef(true);
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            setLicenceModalPreset(null);
            // The webcam mode lives in the shared context: leave the other upload modals in file mode.
            setIsWebcamToUse(false);
        };
    }, []);

    const translate = (key) => language.getValue(`licences.add_modal.${key}`);
    const labelWithCategoryTemplate = language.getValue("licences.label_with_category");
    const getLabel = (option) => formatLicenceLabel(option, labelWithCategoryTemplate || undefined);

    useEffect(() => {
        ApiDatabase.getAllLicence(
            (data) => {
                if (!isMountedRef.current) return;
                const options = buildLicenceCatalogueOptions(data);
                setCatalogueOptions(options);
                setSelectedKey(findLicenceOption(options, preset)?.key ?? null);
                setCatalogueStatus("ready");
            },
            () => {
                if (isMountedRef.current) setCatalogueStatus("error");
            }
        );
    }, [preset]);

    const visibleOptions = filterLicenceOptions(catalogueOptions, searchQuery, getLabel);
    const selectedOption = catalogueOptions.find((option) => option.key === selectedKey) || null;
    const errorMessage = errorKey ? translate(`errors.${errorKey}`) : "";

    const closeModal = () => {
        if (isUploading) return;
        toggle();
    };

    const selectFile = (event) => {
        const selectedFile = event?.target?.files?.[0] || null;
        setFile(selectedFile);
        setErrorKey(null);
        // A capture ends the webcam mode: the photo preview takes its place.
        if (selectedFile) setIsWebcamToUse(false);
    };

    const uploadLicence = () => {
        if (!selectedOption || !file || isUploading) return;
        setErrorKey(null);
        setIsUploading(true);

        // The refused entry is only replaced by the same licence; any other choice is a new one.
        const replacedLicenceId =
            preset?.replacedLicenceId && isSameLicence(selectedOption, preset) ? preset.replacedLicenceId : null;

        ApiDatabase.uploadLicenceFile(
            {
                file,
                licence: selectedOption.licence,
                category: selectedOption.category,
                replacedLicenceId,
            },
            () => {
                toast.success(translate("success"));
                refreshLoadWorker();
                if (!isMountedRef.current) return;
                setIsUploading(false);
                toggle();
            },
            (error) => {
                if (!isMountedRef.current) return;
                setErrorKey(toErrorKey(error));
                setIsUploading(false);
            }
        );
    };

    if (step === "conditions" && selectedOption && file) {
        return (
            <LicenceConditionsStep
                translate={translate}
                isUploading={isUploading}
                errorMessage={errorMessage}
                onClose={closeModal}
                onCancel={() => {
                    if (isUploading) return;
                    setErrorKey(null);
                    setStep("photo");
                }}
                onConfirm={uploadLicence}
            />
        );
    }

    if (step === "photo" && selectedOption) {
        return (
            <LicencePhotoStep
                translate={translate}
                licenceLabel={getLabel(selectedOption)}
                file={file}
                onFileChange={selectFile}
                onRetake={() => setFile(null)}
                isWebcamToUse={isWebcamToUse}
                onToggleWebcam={() => setIsWebcamToUse(!isWebcamToUse)}
                onClose={closeModal}
                onBack={() => {
                    setIsWebcamToUse(false);
                    setStep("catalogue");
                }}
                onValidate={() => setStep("conditions")}
            />
        );
    }

    return (
        <LicenceCatalogueStep
            translate={translate}
            options={visibleOptions}
            catalogueStatus={catalogueStatus}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            selectedKey={selectedKey}
            onSelect={setSelectedKey}
            getLabel={getLabel}
            onClose={closeModal}
            onNext={() => {
                if (selectedOption) setStep("photo");
            }}
        />
    );
};

export default Modallicence;
